import "@shoelace-style/shoelace/dist/components/details/details.js";
import "@shoelace-style/shoelace/dist/components/checkbox/checkbox.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";

import { TemplateResult, html } from "lit";
import { state, property } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { keyed } from "lit/directives/keyed.js";
import { BaseElement } from "../BaseElement";
import {
    FormBooleanDropdownT,
    FormBooleanOrAllDropdownT,
    FormCheckBoxGroupT,
    FormEnumMultiSelectT,
    FormEnumSelectT,
    FormFieldT,
    FormOptionalNumberT,
    FormRatingT,
    FormSelectT,
    FormTextInputT,
    FormTextAreaT,
    FormDateT,
    FormsT,
} from "./FormTypes";

import { litIf } from "../LitUtils";
import { renderSelect } from "./Select";
import { renderEnumSelect } from "./EnumSelect";
import { ifDefined } from "lit/directives/if-defined.js";
import { formatDateTime } from "../../Format";

import "lit-datetime-picker";

export function showDatePicker(initialValue: string, onUpdated: (v: string) => void) {
    const elem = Object.assign(document.createElement("lit-datetime-picker"));
    elem.setAttribute("value", initialValue);
    elem.setAttribute("open", "true");
    elem.addEventListener("input", (e: Event) => {
        onUpdated((e as CustomEvent).detail.toISOString());
        document.body.removeChild(elem);
    });
    elem.style.zIndex = 10000;
    elem.style.position = "absolute";
    elem.style.color = "black";
    elem.close = () => {
        document.body.removeChild(elem);
    };
    document.body.append(elem);
}

export class Form extends BaseElement {
    @state() protected _object: any;
    @property({ type: Array }) visibleFields: string[] = [];

    constructor(protected readonly _changedEventName: string) {
        super();
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();

        this.addEventListener("ds-item-changed", () => this._raiseFormChanged());
    }

    public updateProperties(props: any) {
        Object.assign(this._object, props);
        this.requestUpdate();
        this._raiseFormChanged();
    }

    protected _raiseFormChanged() {
        this.requestUpdate();
    }

    private _getProp(prop: string) {
        return (this._object as any)[prop];
    }

    private _getPropKeyed(prop: string, convert?: (x: any) => any) {
        const v = keyed(this._object, this._getProp(prop));
        if (convert) {
            return convert(v);
        }
        return v;
    }

    private _setProp(prop: string, v: any) {
        this._object[prop] = v;
        this._raiseFormChanged();
    }

    protected _formChanged<T>(list: string, type: T, checked: boolean) {
        if (checked) {
            (this._object as any)[list].push(type);
        } else {
            (this._object as any)[list] = (this._object as any)[list].filter((x: T) => x !== type);
        }
        this._raiseFormChanged();
    }

    protected _renderBooleanDropdown(field: FormBooleanDropdownT) {
        return html` <sl-select
            size="medium"
            pill
            filled
            label="${field.title}"
            value=${keyed(this._object, (this._object as any)[field.property] ? "Yes" : "No")}
            @sl-change=${(e: any) => {
                (this._object as any)[field.property] = e.target.value === "Yes";
                this._raiseFormChanged();
            }}
        >
            <sl-option value="No">No</sl-option>
            <sl-option value="Yes">Yes</sl-option>
        </sl-select>`;
    }

    protected _renderBooleanOrAllDropdown(field: FormBooleanOrAllDropdownT) {
        const value = (this._object as any)[field.property];
        return html` <sl-select
            pill
            filled
            size="medium"
            label="${field.title}"
            .value=${keyed(this._object, value === undefined ? "All" : value)}
            @sl-change=${(e: any) => {
                if (e.target.value === "All") {
                    (this._object as any)[field.property] = undefined;
                } else {
                    (this._object as any)[field.property] = e.target.value;
                }
                this._raiseFormChanged();
            }}
        >
            <sl-option value="All">All</sl-option>
            <sl-option value="False">False</sl-option>
            <sl-option value="True">True</sl-option>
        </sl-select>`;
    }

    protected _renderCheckboxGrid(field: FormCheckBoxGroupT) {
        const prop = (this._object as any)[field.property];
        return html`<div>
            <span class="editor-label">${field.title}</span>
            <div class="checkbox-grid">
                ${field.options.map(
                    (x: any) =>
                        html`<sl-checkbox
                            size="medium"
                            ?checked=${keyed(this._object, !prop || prop.includes(x))}
                            @sl-change=${(e: any) =>
                                this._formChanged(field.property, x, e.target.checked)}
                            >${x}</sl-checkbox
                        >`
                )}
            </div>
        </div>`;
    }

    protected _renderTextInput(field: FormTextInputT) {
        return html`<div>
            <span class="editor-label">${field.title}</span>
            <div class="string-search">
                <sl-input
                    pill
                    filled
                    size="medium"
                    clearable
                    placeholder=${ifDefined(field.placeholder)}
                    value=${keyed(this._object, (this._object as any)[field.property])}
                    @sl-input=${(e: any) => {
                        (this._object as any)[field.property] = e.target.value;
                        this._raiseFormChanged();
                    }}
                ></sl-input>
            </div>
        </div>`;
    }

    protected _renderOptionalNumber(field: FormOptionalNumberT) {
        return html`<div>
            <span class="editor-label">${field.title}</span>
            <div class="string-search">
                <sl-input
                    pill
                    filled
                    size="medium"
                    type="number"
                    clearable
                    placeholder=${field.placeholder}
                    value=${keyed(this._object, (this._object as any)[field.property])}
                    @sl-input=${(e: any) => {
                        (this._object as any)[field.property] =
                            e.target.value.length === 0
                                ? undefined
                                : (parseFloat(e.target.value) as number);
                        this._raiseFormChanged();
                    }}
                ></sl-input>
            </div>
        </div>`;
    }

    protected _renderSelect(field: FormSelectT): TemplateResult {
        let choices =
            field.choices instanceof Function
                ? field.choices(this._object)
                : Array.isArray(field.choices)
                  ? field.choices
                  : [];

        choices = ["All", ...choices];
        return html`${keyed(
            this._object,
            renderSelect(
                this,
                choices,
                this._object,
                field.property,
                field.title,
                undefined,
                false,
                undefined,
                undefined,
                undefined,
                undefined,
                field.displayText
            )
        )}`;
    }

    protected _renderEnumMultiSelect(field: FormEnumMultiSelectT): TemplateResult {
        return html`${keyed(
            this._object,
            renderEnumSelect(
                this,
                field.enumType,
                this._object,
                field.property,
                field.title,
                undefined, //field.helpMsg,
                field.filter,
                true
            )
        )}`;
    }

    protected _renderEnumSelect(field: FormEnumSelectT): TemplateResult {
        return html`${keyed(
            this._object,
            renderEnumSelect(
                this,
                field.enumType,
                this._object,
                field.property,
                field.title,
                undefined, //field.helpMsg,
                field.filter,
                false,
                0,
                undefined,
                field.toEnum,
                field.fromEnum,
                field.displayText
            )
        )}`;
    }

    protected _renderRating(field: FormRatingT): TemplateResult {
        return html`${keyed(
            this._object,
            html`<div>
                <span class="editor-label">${field.title}</span
                ><sl-rating
                    style="display:block"
                    class="editor-grid-item"
                    value=${keyed(this._object, (this._object as any)[field.property])}
                    @sl-change=${(e: any) => {
                        (this._object as any)[field.property] = e.target.value;
                        this._raiseFormChanged();
                    }}
                ></sl-rating>
            </div>`
        )}`;
    }

    protected _renderTextArea(field: FormTextAreaT): TemplateResult {
        return html`${keyed(
            this._object,
            html` <div>
                <span class="editor-label">${field.title}</span
                ><sl-textarea
                    rows="12"
                    value=${keyed(this._object, (this._object as any)[field.property])}
                    @sl-input=${(e: any) => {
                        (this._object as any)[field.property] = e.target.value;
                        this._raiseFormChanged();
                    }}
                ></sl-textarea>
            </div>`
        )}`;
    }

    protected _renderDate(field: FormDateT): TemplateResult {
        return html` <div>
            <span class="editor-label">${field.title}</span>
            <div
                class="flex-row"
                @click=${() => {
                    showDatePicker(this._getProp(field.property), (v: string) => {
                        this._setProp(field.property, v);
                    });
                }}
            >
                <span>
                    ${keyed(this._object, formatDateTime((this._object as any)[field.property]))}
                </span>
                <sl-icon name="pencil-fill"></sl-icon>
            </div>
        </div>`;
    }

    protected _showField(field: FormFieldT) {
        if (field.visible) {
            return this.visibleFields.includes(field.visible);
        }

        return true;
    }

    protected _renderForm(form: FormsT[]) {
        return html`${litIf(
            this._object,
            () =>
                html`${form
                    .filter((field) => this._showField(field))
                    .map((field) =>
                        choose(field.type, [
                            [
                                "checkboxGroup",
                                () => this._renderCheckboxGrid(field as FormCheckBoxGroupT),
                            ],
                            ["textInput", () => this._renderTextInput(field as FormTextInputT)],
                            [
                                "booleanDropdown",
                                () => this._renderBooleanDropdown(field as FormBooleanDropdownT),
                            ],
                            [
                                "booleanOrAllDropdown",
                                () =>
                                    this._renderBooleanOrAllDropdown(
                                        field as FormBooleanOrAllDropdownT
                                    ),
                            ],
                            [
                                "optionalNumber",
                                () => this._renderOptionalNumber(field as FormOptionalNumberT),
                            ],
                            [
                                "enumMultiSelect",
                                () => this._renderEnumMultiSelect(field as FormEnumMultiSelectT),
                            ],
                            ["enumSelect", () => this._renderEnumSelect(field as FormEnumSelectT)],
                            ["rating", () => this._renderRating(field as FormRatingT)],
                            ["select", () => this._renderSelect(field as FormSelectT)],
                            ["textArea", () => this._renderTextArea(field as FormTextAreaT)],
                            ["date", () => this._renderDate(field as FormDateT)],
                        ])
                    )} `
        )}`;
    }
}
